<template>
  <div class="prompt-management">
    <div class="toolbar">
      <el-button type="primary" @click="showAddDialog">添加提示词</el-button>
      <el-input
        v-model="searchKeyword"
        placeholder="搜索标题、角色或提示词"
        clearable
        style="width: 300px; margin-left: 10px;"
        @input="handleSearch"
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-select
        v-model="selectedScene"
        placeholder="筛选场景"
        clearable
        style="width: 150px; margin-left: 10px;"
        @change="handleFilterChange"
      >
        <el-option
          v-for="scene in scenes"
          :key="scene"
          :label="scene"
          :value="scene"
        />
      </el-select>
    </div>

    <el-table :data="displayItems" border v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="title" label="标题">
        <template #default="{ row }">
          <span style="display: flex; align-items: center; gap: 8px;">
            <el-tag v-if="row.is_pinned" type="warning" size="small">置顶</el-tag>
            <span>{{ row.title }}</span>
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="scene" label="场景" width="120">
        <template #default="{ row }">
          <el-tag v-if="row.scene" type="info" size="small">{{ row.scene }}</el-tag>
          <span v-else style="color: rgba(255, 255, 255, 0.5);">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="role" label="角色" width="200">
        <template #default="{ row }">
          <div class="text-ellipsis" :title="row.role">{{ row.role }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="prompt" label="提示词" width="300">
        <template #default="{ row }">
          <div class="text-ellipsis" :title="row.prompt">{{ row.prompt }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="is_pinned" label="置顶" width="100">
        <template #default="{ row }">
          <el-switch
            v-model="row.is_pinned"
            @change="togglePinned(row)"
            active-text="置顶"
            inactive-text=""
          />
        </template>
      </el-table-column>
      <el-table-column prop="is_active" label="启用" width="100">
        <template #default="{ row }">
          <el-switch
            v-model="row.is_active"
            @change="toggleStatus(row)"
            active-text="启用"
            inactive-text=""
          />
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间" width="180">
        <template #default="{ row }">
          {{ formatDate(row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200">
        <template #default="{ row }">
          <el-button size="small" @click="editItem(row)">编辑</el-button>
          <el-button size="small" type="danger" @click="deleteItem(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="total > 0"
      :current-page="currentPage"
      :page-sizes="[20, 50, 100, { value: -1, label: '全部' }]"
      :page-size="pageSize"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
      class="pagination"
    />

    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="900px">
      <el-form :model="form" label-width="100px">
        <el-form-item label="标题" required>
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="场景">
          <el-input v-model="form.scene" placeholder="如：写作、编程、翻译等" />
        </el-form-item>
        <el-form-item label="角色" required>
          <el-input v-model="form.role" type="textarea" :rows="4" />
        </el-form-item>
        <el-form-item label="提示词" required>
          <el-input v-model="form.prompt" type="textarea" :rows="8" />
        </el-form-item>
        <el-form-item label="置顶">
          <el-switch v-model="form.is_pinned" active-text="置顶" inactive-text="" />
          <div class="form-tip">置顶的提示词会优先显示在列表前面</div>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.is_active" active-text="启用" inactive-text="" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveItem">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../api'
import { Search } from '@element-plus/icons-vue'

export default {
  name: 'PromptManagement',
  components: {
    Search
  },
  data() {
    return {
      items: [],
      scenes: [],
      searchKeyword: '',
      selectedScene: null,
      currentPage: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      dialogVisible: false,
      dialogTitle: '添加提示词',
      form: {
        title: '',
        scene: '',
        role: '',
        prompt: '',
        is_active: true
      },
      currentId: null
    }
  },
  computed: {
    filteredItems() {
      let result = this.items
      
      // 按场景筛选
      if (this.selectedScene) {
        result = result.filter(item => item.scene === this.selectedScene)
      }
      
      // 模糊搜索
      if (this.searchKeyword) {
        const keyword = this.searchKeyword.toLowerCase()
        result = result.filter(item => {
          return (
            (item.title && item.title.toLowerCase().includes(keyword)) ||
            (item.role && item.role.toLowerCase().includes(keyword)) ||
            (item.prompt && item.prompt.toLowerCase().includes(keyword))
          )
        })
      }
      
      return result
    },
    displayItems() {
      return this.items
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize === -1 ? 'all' : this.pageSize
        }
        
        if (this.selectedScene) {
          params.scene = this.selectedScene
        }
        
        if (this.searchKeyword) {
          params.search = this.searchKeyword
        }
        
        const response = await api.get('/admin/prompts/templates/', { params })
        
        if (Array.isArray(response.data)) {
          this.items = response.data
          this.total = response.data.length
        } else {
          this.items = response.data.results || []
          this.total = response.data.count || 0
        }
        
        // 提取所有场景
        const sceneSet = new Set()
        this.items.forEach(item => {
          if (item.scene) {
            sceneSet.add(item.scene)
          }
        })
        this.scenes = Array.from(sceneSet).sort()
      } catch (error) {
        this.$message.error('加载失败')
        this.items = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    showAddDialog() {
      this.dialogTitle = '添加提示词'
      this.currentId = null
      this.form = {
        title: '',
        scene: '',
        role: '',
        prompt: '',
        is_pinned: false,
        is_active: true
      }
      this.dialogVisible = true
    },
    editItem(item) {
      this.dialogTitle = '编辑提示词'
      this.currentId = item.id
      this.form = {
        title: item.title,
        scene: item.scene || '',
        role: item.role,
        prompt: item.prompt,
        is_pinned: item.is_pinned || false,
        is_active: item.is_active !== undefined ? item.is_active : true
      }
      this.dialogVisible = true
    },
    async deleteItem(item) {
      try {
        await this.$confirm(`确定要删除提示词"${item.title}"吗？`, '提示', {
          type: 'warning'
        })
        await api.delete(`/admin/prompts/templates/${item.id}/`)
        this.$message.success('删除成功')
        this.loadItems()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    async saveItem() {
      try {
        if (!this.form.title) {
          this.$message.warning('请输入标题')
          return
        }
        if (!this.form.role) {
          this.$message.warning('请输入角色')
          return
        }
        if (!this.form.prompt) {
          this.$message.warning('请输入提示词')
          return
        }
        
        if (this.currentId) {
          await api.patch(`/admin/prompts/templates/${this.currentId}/`, this.form)
          this.$message.success('更新成功')
        } else {
          await api.post('/admin/prompts/templates/', this.form)
          this.$message.success('添加成功')
        }
        this.dialogVisible = false
        this.loadItems()
      } catch (error) {
        console.error('保存失败:', error)
        const errorMsg = error.response?.data?.detail || error.response?.data?.error || '保存失败'
        this.$message.error(errorMsg)
      }
    },
    async toggleStatus(item) {
      try {
        await api.patch(`/admin/prompts/templates/${item.id}/`, {
          is_active: item.is_active
        })
        this.$message.success(item.is_active ? '已启用' : '已禁用')
      } catch (error) {
        item.is_active = !item.is_active
        this.$message.error('状态更新失败')
        console.error(error)
      }
    },
    async togglePinned(item) {
      try {
        await api.patch(`/admin/prompts/templates/${item.id}/`, {
          is_pinned: item.is_pinned
        })
        this.$message.success(item.is_pinned ? '已置顶' : '已取消置顶')
        this.loadItems() // 重新加载以更新排序
      } catch (error) {
        // 恢复原状态
        item.is_pinned = !item.is_pinned
        this.$message.error('置顶状态更新失败')
        console.error(error)
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.loadItems()
    },
    handleFilterChange() {
      this.currentPage = 1
      this.loadItems()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadItems()
    },
    handlePageChange(page) {
      this.currentPage = page
      if (this.pageSize !== -1) {
        this.loadItems()
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.prompt-management {
  width: 100%;
  color: #e0e0e0;
}

.toolbar {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.text-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-tip {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 5px;
}
</style>


